import { useState } from 'react'
import { useInterromperLote, useLoteAtual } from '../../../api/queries/loteDeAnalise'
import { Botao } from '../../../components/Botao'
import { Dialogo } from '../../../components/Dialogo'
import { AvisoDeErro } from '../../../components/Estados'
import { CampoDeTextoLongo } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import { competenciaPorExtenso } from '../../chamados/competencia'
import { emUnidades } from '../credito-da-ia/dinheiro'

/** Cinco itens por rodada, uma rodada por minuto — o ritmo do dreno no servidor. */
const ITENS_POR_MINUTO = 5

const MINIMO_DO_MOTIVO = 10

/**
 * O lote em andamento.
 *
 * **Pausado no teto NÃO parece falha**, e é decisão: não há `role="alert"` nem vermelho aqui. O lote
 * volta a rodar sozinho se a administração declarar um crédito maior ainda dentro do mesmo mês;
 * nada foi perdido e nada será refeito. Tratá-lo como erro faria alguém abrir um chamado no suporte
 * por causa de um comportamento correto.
 *
 * **Interromper fica sempre habilitado.** Quem quer parar um gasto em massa não pode esbarrar num
 * botão desabilitado porque a tela está entre dois estados.
 *
 * @returns o andamento
 */
export function AndamentoDoLote() {
  const t = TEXTOS.loteDeAnalise
  const { data: lote } = useLoteAtual()
  const [parando, setParando] = useState(false)
  const [motivo, setMotivo] = useState('')
  const interromper = useInterromperLote()

  if (!lote) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
        {t.andamento.nenhum}
      </section>
    )
  }

  const feitos = lote.itens - lote.pendentes
  const minutos = Math.ceil(lote.pendentes / ITENS_POR_MINUTO)

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="text-base font-semibold text-slate-900">
        {t.andamento.titulo(competenciaPorExtenso(lote.competencia), feitos, lote.itens)}
      </h2>

      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-200" aria-hidden="true">
        <div
          className="h-full bg-niteroi-600"
          style={{ width: `${lote.itens === 0 ? 0 : (feitos / lote.itens) * 100}%` }}
        />
      </div>

      <p className="mt-2 text-sm tabular-nums text-slate-700">
        {t.andamento.contagem(lote.concluidos, lote.reaproveitados, lote.falhas, lote.adiados, lote.pendentes)}
      </p>
      <p className="mt-1 text-sm tabular-nums text-slate-700">{t.andamento.gasto(emUnidades(lote.tokens))}</p>
      {/* A ressalva ao lado do número, e não em vez dele. Hoje, em produção, os seis pareceres
          emitidos têm `tokens_de_entrada` nulo: sem esta linha o lote escrevia "Gasto até agora: 0
          unidades de uso" enquanto pagava a Azure, eventualmente logo acima de "Pausado: o limite do
          mês foi atingido" — duas frases que se contradizem na mesma tela. Âmbar e sem
          `role="alert"`, como a tabela de uso já faz: não é falha, é o que o provedor não informou. */}
      {lote.semMedicao > 0 && (
        <p className="mt-1 max-w-prose text-xs text-amber-800">{t.andamento.gastoSemMedicao(lote.semMedicao)}</p>
      )}
      {lote.pendentes > 0 && lote.situacao === 'EM_ANDAMENTO' && (
        <p className="mt-1 text-xs text-slate-500">{t.andamento.ritmo(minutos)}</p>
      )}

      <p className="mt-3 max-w-prose rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
        {t.situacaoDoLote[lote.situacao]}
      </p>

      {(lote.situacao === 'EM_ANDAMENTO' || lote.situacao === 'PAUSADO_NO_TETO') && (
        <Botao aparencia="perigo" className="mt-3" onClick={() => setParando(true)}>
          {t.andamento.interromper}
        </Botao>
      )}

      {parando && (
        <Dialogo
          titulo={t.andamento.interromper}
          descricao={t.andamento.naoReabre}
          aoFechar={() => setParando(false)}
          rodape={
            <>
              <Botao aparencia="secundario" onClick={() => setParando(false)}>
                Cancelar
              </Botao>
              <Botao
                aparencia="perigo"
                disabled={motivo.trim().length < MINIMO_DO_MOTIVO || interromper.isPending}
                onClick={() =>
                  interromper.mutate(
                    { id: lote.loteId, motivo: motivo.trim() },
                    { onSuccess: () => setParando(false) },
                  )
                }
              >
                {t.andamento.interromper}
              </Botao>
            </>
          }
        >
          <CampoDeTextoLongo
            rotulo={t.andamento.interromperMotivo}
            rows={3}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
          {interromper.isError && <AvisoDeErro erro={interromper.error} />}
        </Dialogo>
      )}
    </section>
  )
}
